import { chakra } from "@chakra-ui/react";
import React from "react";
import { answerAsString, firstAnswer, formatAmericanDate } from "./answerFormat";
import { resolveSignature, type SignatureValue } from "./blockFormat";
import { Handwriting, PrintBlock } from "./printStyles";
import type { QuestionViewProps } from "./types";

// employeeSignature, patientSignature, customSignature -> the PDF's row of Signature cells
// (name / signature image / date), each caption under a 2px line.

const SignatureCellRoot = chakra("div", {
  baseStyle: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    flexDirection: "column",
    gap: "12px",
    justifyContent: "flex-end",
    minHeight: "40px",
    minWidth: 0,
  },
});

const SignatureCaption = chakra("div", {
  baseStyle: {
    borderTop: "2px solid",
    boxSizing: "border-box",
    p: "8px",
    textAlign: "center",
    width: "100%",
  },
});

const SignatureImage = chakra("img", {
  baseStyle: {
    height: "80px",
    maxWidth: "85%",
    objectFit: "contain",
    transform: "translateY(50%)",
  },
});

function SignatureCell(props: { caption: string; children?: React.ReactNode }) {
  return (
    <SignatureCellRoot>
      <chakra.div display="flex" justifyContent="center" textAlign="center">
        {props.children}
      </chakra.div>
      <SignatureCaption>{props.caption}</SignatureCaption>
    </SignatureCellRoot>
  );
}

function SignatureMark(props: { signeeName: string; value: SignatureValue }) {
  switch (props.value.type) {
    case "empty":
      return null;
    case "image":
      return <SignatureImage alt="Signature" src={props.value.url} />;
    case "consent":
      return <Handwriting>{props.signeeName}</Handwriting>;
    case "text":
      // e.g. the reason the signee did not sign
      return <span>{props.value.text}</span>;
  }
}

/**
 * The PDF prints the document submission date; the closest per-question value we have is when
 * the signature was answered.
 */
function signedDate(props: QuestionViewProps): string {
  return formatAmericanDate(props.block.answers[0]?.answeredAt ?? null);
}

function customSigneeName(props: QuestionViewProps): string {
  const nameQuestion = props.block.question.columns?.[0]?.[0];
  if (nameQuestion === undefined) {
    return "Signee";
  }
  const answers = props.answers.get(nameQuestion.id);
  if (answers === undefined || answers.length === 0) {
    return "";
  }
  return answerAsString(answers[0].answer) ?? "Signee";
}

export function SignatureQuestionView(props: QuestionViewProps) {
  const { block, placeholders } = props;
  const value = resolveSignature(firstAnswer(block.answers));
  const date = signedDate(props);

  const cells = (() => {
    switch (block.question.itemType) {
      case "employeeSignature": {
        // A non-image answer is the name the employee typed instead of signing.
        const name = value.type === "text" ? value.text : placeholders.caregiverName ?? "";
        const mark: SignatureValue = value.type === "text" ? { type: "empty" } : value;
        return [
          <SignatureCell key="name" caption="Employee Name">
            <strong>{name}</strong>
          </SignatureCell>,
          <SignatureCell key="signature" caption="Signature">
            <SignatureMark signeeName={name} value={mark} />
          </SignatureCell>,
        ];
      }
      case "customSignature": {
        const signeeName = customSigneeName(props);
        return [
          block.question.hideNameFieldSignature === true ? null : (
            <SignatureCell key="name" caption="Signee">
              <strong>{signeeName}</strong>
            </SignatureCell>
          ),
          <SignatureCell key="signature" caption="Signature">
            <SignatureMark signeeName={signeeName} value={value} />
          </SignatureCell>,
        ];
      }
      default:
        return [
          <SignatureCell key="name" caption="Patient Name">
            <strong>{placeholders.patientName}</strong>
          </SignatureCell>,
          <SignatureCell key="signature" caption="Patient/Representative Signature">
            <SignatureMark signeeName={placeholders.patientName} value={value} />
          </SignatureCell>,
        ];
    }
  })();

  return (
    <PrintBlock display="flex" gap="16px">
      {cells}
      <SignatureCell caption="Date">
        <strong>{date}</strong>
      </SignatureCell>
    </PrintBlock>
  );
}
